import FirebaseLogin from "@/lib/auth/AuthApi.service";
import { BaseApiClient } from "@/lib/api/baseApiClient.service";
import type { JWTPayload, User } from "@/lib/auth/auth.types";
import type { User as FirebaseUser } from "firebase/auth";
import { jwtDecode } from "jwt-decode";
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
} from "react";
import { useCookies } from "react-cookie";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useFirebaseAuthState } from "../hooks/useFirebaseAuthState.hook";
import i18n from "@/lib/i18n";
import { AnalyticsService } from "@/lib/analytics/analytics.service";
import { captureAuthException } from "@/lib/auth/captureAuthException";

interface AuthContextType {
	isAuthenticated: boolean;
	isLoading: boolean;
	user: User | null;
	login: (email: string, password: string) => Promise<void>;
	loginWithGoogle: (refreshCaptcha?: () => Promise<string>) => Promise<void>;
	loginWithApple: () => Promise<void>;
	logout: () => Promise<void>;
	setAuthToken: (token: string) => Promise<void>;
	refreshUserAndToken: () => Promise<boolean>;
	refreshUserProfile: () => Promise<boolean>;
	isRefreshing: boolean;
	firebaseUser: FirebaseUser | null;
}

const AuthContext = createContext<AuthContextType | null>(null);

const STORAGE_KEY_AUTH_TAB_LOCK = "taxpeer_auth_tab_lock";
const TAB_LOCK_TIMEOUT = 5000; // 5 seconds

/**
 * Authentication Context for the Taxpeer application
 *
 * React Context is a way to share state and functionality across components without
 * having to pass props manually through each level (prop drilling). It creates a
 * global state that can be accessed by any component within its provider.
 *
 * This AuthContext specifically manages:
 * - Authentication state (isAuthenticated)
 * - Loading state during authentication checks
 * - User information from the JWT token
 * - Login functionality with Firebase
 * - Logout functionality
 * - JWT token management in cookies
 * - Firebase auth state change monitoring
 * - User data refresh functionality
 *
 * Usage:
 * 1. Wrap your app with AuthProvider:
 *    ```tsx
 *    <AuthProvider>
 *      <App />
 *    </AuthProvider>
 *    ```
 *
 * 2. Use the auth context in any component:
 *    ```tsx
 *    const { isAuthenticated, user, login, logout, refreshUserAndToken } = useAuth();
 *    ```
 *
 * Authentication Flow:
 * 1. User attempts login with email/password
 * 2. Firebase authenticates credentials
 * 3. Firebase returns a token
 * 4. Token is stored in cookies with secure settings
 * 5. Token is used for subsequent API requests
 *
 * Security Features:
 * - Tokens are stored in HTTP-only cookies (with secure flag in production)
 * - Strict same-site policy
 * - 7-day token expiration
 * - Auto-logout on Firebase auth state changes (e.g., email verification)
 *
 * @see FirebaseLogin for the underlying Firebase authentication implementation
 * @see RequireAuth component for protecting routes
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
	const [isLoading, setIsLoading] = useState(true);
	const [isRefreshing, setIsRefreshing] = useState(false);
	const [isSettingToken, setIsSettingToken] = useState(false);
	// Always start true: Safari/iOS may wipe sessionStorage during Google/Apple
	// redirect, so we must await getRedirectResult() on every mount regardless of
	// our local pending_auth_redirect flag.
	const [isCheckingRedirect, setIsCheckingRedirect] = useState(true);
	const [cookies, setCookie, removeCookie] = useCookies(["jwt_token"]);
	const [user, setUser] = useState<User | null>(null);
	const navigate = useNavigate();
	const firebaseLogin = FirebaseLogin.getInstance();

	// Generate unique tab ID for debugging multi-tab issues
	const [tabId] = useState(
		() => `tab-${Math.random().toString(36).substr(2, 9)}`,
	);

	// Use our custom Firebase auth state hook
	const { firebaseUser, loading: firebaseLoading } = useFirebaseAuthState();

	/**
	 * Acquires a lock to prevent multiple tabs from performing auth operations simultaneously
	 */
	const acquireAuthLock = useCallback((): boolean => {
		const now = Date.now();
		const existingLock = localStorage.getItem(STORAGE_KEY_AUTH_TAB_LOCK);

		if (existingLock) {
			const { tabId: lockTabId, timestamp } = JSON.parse(existingLock);

			// If lock is held by this tab or expired, we can proceed
			if (lockTabId === tabId || now - timestamp > TAB_LOCK_TIMEOUT) {
				localStorage.setItem(
					STORAGE_KEY_AUTH_TAB_LOCK,
					JSON.stringify({ tabId, timestamp: now }),
				);
				return true;
			}

			return false;
		}

		// No existing lock, acquire it
		localStorage.setItem(
			STORAGE_KEY_AUTH_TAB_LOCK,
			JSON.stringify({ tabId, timestamp: now }),
		);
		console.log("Acquired auth lock");
		return true;
	}, [tabId]);

	/**
	 * Releases the auth lock
	 */
	const releaseAuthLock = useCallback((): void => {
		const existingLock = localStorage.getItem(STORAGE_KEY_AUTH_TAB_LOCK);
		if (existingLock) {
			const { tabId: lockTabId } = JSON.parse(existingLock);
			if (lockTabId === tabId) {
				localStorage.removeItem(STORAGE_KEY_AUTH_TAB_LOCK);
			}
		}
	}, [tabId]);

	/**
	 * This function sets the auth token in the cookies and BaseApiClient
	 *
	 * @param token
	 */
	const setAuthToken = useCallback(
		async (token: string) => {
			// Try to acquire lock for multi-tab coordination
			if (!acquireAuthLock()) {
				return;
			}

			setIsSettingToken(true);

			try {
				// Set token immediately in BaseApiClient for instant availability
				const baseApiClient = BaseApiClient.getInstance();
				baseApiClient.setAuthToken(token);

				// Fetch full user profile (including subscription limits)
				const profile = await firebaseLogin.getMe();
				const decodedToken = jwtDecode<JWTPayload>(token);
				setUser({ ...decodedToken, ...profile });

				// Link this user to all analytics providers (PostHog + GTM)
				AnalyticsService.identify(decodedToken.uid, {
					email: decodedToken.email,
					displayName: decodedToken.displayName,
					subscriptionStatus: profile.subscriptionStatus,
					currentPlanId: profile.currentPlanId,
				});

				// Use ONLY react-cookie's setCookie for consistency
				// The issue might be conflict between manual and react-cookie setting
				setCookie("jwt_token", token, {
					path: "/",
					maxAge: 7 * 24 * 60 * 60, // 7 days
					secure: process.env.NODE_ENV === "production",
					sameSite: "strict",
				});

				// Force a small delay to allow react-cookie to process
				await new Promise((resolve) => setTimeout(resolve, 50));

				// Verify cookie was set
				const cookieSetSuccessfully = document.cookie.includes("jwt_token=");

				if (!cookieSetSuccessfully) {
					throw new Error("Failed to set authentication cookie");
				}
			} finally {
				setIsSettingToken(false);
				releaseAuthLock();
			}
		},
		[setCookie, acquireAuthLock, releaseAuthLock, firebaseLogin],
	);

	/**
	 * Refreshes the user's JWT token by getting a new Firebase token
	 * and exchanging it for a new JWT token from the backend
	 *
	 * This is useful when user data has changed (like display name or email verification status)
	 * and you need to update the JWT token without requiring a full logout and login.
	 *
	 * @returns A promise that resolves to true if the refresh was successful, false otherwise
	 */
	const refreshUserAndToken = useCallback(async (): Promise<boolean> => {
		try {
			setIsRefreshing(true);

			// Get the current Firebase user
			const currentUser = firebaseLogin.auth.currentUser;

			if (!currentUser) {
				toast.error(i18n.t("authService.noUserSignedIn"));
				return false;
			}

			// Get a fresh Firebase token
			const firebaseToken = await currentUser.getIdToken(true);

			// Exchange it for a new JWT (no Turnstile — dedicated refresh endpoint)
			const response = await firebaseLogin.refreshUserToken(firebaseToken);

			// Update the token in the auth context
			await setAuthToken(response.token);
			console.log("Refreshed user token");
			return true;
		} catch (error) {
			console.error("Failed to refresh user token:", error);
			captureAuthException(error, "context_refresh_user_token");
			// failed to refresh user token, so we need to log out
			logout();
			return false;
		} finally {
			setIsRefreshing(false);
		}
	}, [firebaseLogin, setAuthToken]);

	/**
	 * Refreshes only user profile data from /session/me (no token exchange).
	 * Use this after billing/subscription updates to avoid auth/login side effects.
	 */
	const refreshUserProfile = useCallback(async (): Promise<boolean> => {
		try {
			if (!cookies.jwt_token) {
				return false;
			}

			const baseApiClient = BaseApiClient.getInstance();
			baseApiClient.setAuthToken(cookies.jwt_token);

			const profile = await firebaseLogin.getMe();
			setUser((prev) => {
				if (!prev) return prev;
				return { ...prev, ...profile };
			});

			return true;
		} catch (error) {
			console.error("Failed to refresh user profile:", error);
			captureAuthException(error, "context_refresh_user_profile");
			return false;
		}
	}, [cookies.jwt_token, firebaseLogin]);

	/**
	 * Logs in the user with the given email and password
	 * @param email
	 * @param password
	 */
	const login = async (email: string, password: string) => {
		const response = await firebaseLogin.login({ email, password });
		await setAuthToken(response.token);
		navigate("/dashboard");
	};

	/**
	 * Logs in the user with Google authentication.
	 * Prefers popup; falls back to redirect only if the popup is blocked.
	 */
	const loginWithGoogle = async (refreshCaptcha?: () => Promise<string>) => {
		try {
			const response = await firebaseLogin.loginWithGoogle(refreshCaptcha);
			if (!response) return; // redirect or in-app-browser escape
			await setAuthToken(response.token);
			navigate("/dashboard");
		} catch (error) {
			console.error("Google login failed:", error);
			captureAuthException(error, "context_login_google");
			throw error;
		}
	};

	/**
	 * Logs in the user with Apple authentication.
	 * Prefers popup; falls back to redirect only if the popup is blocked.
	 */
	const loginWithApple = async () => {
		try {
			const response = await firebaseLogin.loginWithApple();
			if (!response) return; // redirect flow – page is navigating away
			await setAuthToken(response.token);
			navigate("/dashboard");
		} catch (error) {
			console.error("Apple login failed:", error);
			captureAuthException(error, "context_login_apple");
			throw error;
		}
	};

	// logs the user out and removes the cookie
	const logout = useCallback(async () => {
		// Try to acquire lock for multi-tab coordination
		if (!acquireAuthLock()) {
			console.log("Cannot logout - another tab is handling auth");
			return;
		}

		try {
			await firebaseLogin.logout();

			// Reset analytics identity so subsequent events are anonymous
			AnalyticsService.reset();

			// Reset BaseApiClient to clean state to avoid stale auth issues
			BaseApiClient.resetInstance();
			console.log("BaseApiClient instance reset after logout");

			// we need to remove the cookie with the same path, otherwise it will not be removed properly or may not be removed at all
			removeCookie("jwt_token", {
				path: "/",
				secure: process.env.NODE_ENV === "production",
				sameSite: "strict",
			});

			// Clear the user state and localStorage
			setUser(null);

			navigate("/auth/login");
		} finally {
			releaseAuthLock();
		}
	}, [firebaseLogin, removeCookie, navigate, acquireAuthLock, releaseAuthLock]);

	// This actually checks if the user is authenticated and decodes the JWT token to get user information
	useEffect(() => {
		const baseApiClient = BaseApiClient.getInstance();

		const initUser = async () => {
			if (cookies.jwt_token) {
				try {
					// Set token in BaseApiClient for immediate availability
					baseApiClient.setAuthToken(cookies.jwt_token);

					// Decode the JWT token first
					const decodedToken = jwtDecode<JWTPayload>(cookies.jwt_token);
					setUser(decodedToken);

					// Fetch full profile (including subscription limits) in background
					try {
						const profile = await firebaseLogin.getMe();
						setUser({ ...decodedToken, ...profile });

						// Re-identify on page load so PostHog links the session
						AnalyticsService.identify(decodedToken.uid, {
							email: decodedToken.email,
							displayName: decodedToken.displayName,
							subscriptionStatus: profile.subscriptionStatus,
							currentPlanId: profile.currentPlanId,
						});
					} catch (e) {
						console.error("Failed to fetch user profile:", e);
						captureAuthException(e, "context_init_user_profile");
						// Keep basic JWT data if profile fetch fails
						// Still identify with basic JWT info
						AnalyticsService.identify(decodedToken.uid, {
							email: decodedToken.email,
							displayName: decodedToken.displayName,
						});
					}
				} catch (_) {
					captureAuthException(_, "context_init_user_jwt");
					setUser(null);
					baseApiClient.clearAuthToken();
				}
			} else {
				setUser(null);
				baseApiClient.clearAuthToken();
			}
			setIsLoading(false);
		};

		initUser();
	}, [cookies.jwt_token, firebaseLogin]);

	// Handle redirect result from signInWithRedirect (popup-blocked fallback).
	// Always call getRedirectResult on mount — do not gate on sessionStorage.
	// iOS Safari may clear sessionStorage during an OAuth round-trip.
	// Note: redirect itself still needs a same-origin authDomain/proxy to work
	// reliably on Safari 16.1+; popup is the primary path.
	// biome-ignore lint/correctness/useExhaustiveDependencies: intentionally run once on mount
	useEffect(() => {
		const handleRedirect = async () => {
			try {
				sessionStorage.removeItem("pending_auth_redirect");
				const result = await firebaseLogin.handleRedirectResult();
				if (result) {
					await setAuthToken(result.token);

					const redirectUrl = localStorage.getItem("auth_redirect");
					const goToShop = localStorage.getItem("go_to_shop");

					if (redirectUrl) {
						localStorage.removeItem("auth_redirect");
						navigate(redirectUrl);
					} else if (goToShop) {
						localStorage.removeItem("go_to_shop");
						navigate("/shop");
					} else {
						navigate("/dashboard");
					}
				}
			} catch (error) {
				console.error("[Auth] Redirect result handling failed:", error);
				captureAuthException(error, "context_handle_redirect");
			} finally {
				setIsCheckingRedirect(false);
			}
		};

		handleRedirect();
	}, []);

	// Handle Firebase auth state changes
	// biome-ignore lint/correctness/useExhaustiveDependencies: dependencies intentionally curated for auth sync
	useEffect(() => {
		// Skip if still loading Firebase auth state or processing a redirect
		if (firebaseLoading || isCheckingRedirect) {
			return;
		}

		// If we are in an anonymous Firebase session, we should never keep a JWT cookie around.
		// This avoids "JWT user" + "anonymous Firebase user" mismatches (especially in the signup funnel).
		if (firebaseUser?.isAnonymous && cookies.jwt_token && !isSettingToken) {
			console.log("Anonymous Firebase user detected; clearing JWT cookie", {
				isAnonymous: firebaseUser.isAnonymous,
				jwtTokenPresent: !!cookies.jwt_token,
				isSettingToken,
				timestamp: new Date().toISOString(),
			});

			BaseApiClient.getInstance().clearAuthToken();
			removeCookie("jwt_token", {
				path: "/",
				secure: process.env.NODE_ENV === "production",
				sameSite: "strict",
			});
			setUser(null);
			return;
		}

		// If we have a JWT token but no Firebase user, log out
		// BUT add protection against immediate logouts during token setting
		if (!firebaseUser && cookies.jwt_token && !isSettingToken) {
			console.log("Firebase user is null but JWT token exists, logging out", {
				firebaseUserWas: firebaseUser,
				jwtTokenPresent: !!cookies.jwt_token,
				isSettingToken,
				callStack: new Error().stack,
				timestamp: new Date().toISOString(),
			});
			logout();
			return;
		}

		// If we have both Firebase user and JWT token, check if they're in sync
		if (firebaseUser && cookies.jwt_token) {
			try {
				const decodedToken = jwtDecode<JWTPayload>(cookies.jwt_token);

				// Check if email verification status changed
				if (decodedToken.emailVerified !== firebaseUser.emailVerified) {
					console.log(
						"Email verification status changed, logging out to refresh token",
					);
					toast.success(i18n.t("authService.emailVerified"));
					logout();
					return;
				}

				if (!firebaseUser.email) {
					console.log(
						"Firebase user has no email, we are likely in impersonation mode",
					);
					return;
				}

				if (decodedToken.email !== firebaseUser.email) {
					console.log("Email address changed, logging out to refresh token");
					toast.success(i18n.t("authService.emailChanged"));
					logout();
					return;
				}
			} catch (error) {
				console.error("Error comparing JWT with Firebase user:", error);
				captureAuthException(error, "context_jwt_firebase_sync");
			}
		}
	}, [
		firebaseUser,
		firebaseLoading,
		cookies.jwt_token,
		logout,
		isSettingToken,
		isCheckingRedirect,
	]);

	// Add event listener for token expiration
	useEffect(() => {
		const handleTokenExpired = () => {
			toast.error(i18n.t("authService.sessionExpired"));
			logout();
		};

		window.addEventListener("auth:tokenExpired", handleTokenExpired);
		window.addEventListener("auth:refreshToken", refreshUserAndToken);

		return () => {
			window.removeEventListener("auth:tokenExpired", handleTokenExpired);
			window.removeEventListener("auth:refreshToken", refreshUserAndToken);
		};
	}, [logout, refreshUserAndToken]); // Added logout and refreshUserAndToken to the dependency array

	// Cleanup auth lock when tab closes
	useEffect(() => {
		const handleBeforeUnload = () => {
			releaseAuthLock();
		};

		window.addEventListener("beforeunload", handleBeforeUnload);

		return () => {
			window.removeEventListener("beforeunload", handleBeforeUnload);
			releaseAuthLock();
		};
	}, [releaseAuthLock]);

	const isAuthenticatedValue = !!cookies.jwt_token;

	const value = {
		isAuthenticated: isAuthenticatedValue,
		isLoading:
			isLoading || firebaseLoading || isSettingToken || isCheckingRedirect,
		isRefreshing,
		user,
		login,
		loginWithGoogle,
		loginWithApple,
		logout,
		setAuthToken,
		refreshUserAndToken,
		refreshUserProfile,
		firebaseUser,
	};

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
};
